import { updateSearchQuery } from "../../../../@nearbites.com/query/search-query";
import React, { FunctionComponent, useRef } from "react";
import { GpsLocation, SearchQuery, Localities } from "../../../../@nearbites.com/query";
import { GpsMd } from "../../../../@nitty";
import FilterPill from "../filter-pill";
import { fetchingData } from "../../../../util/appEvents";
import { useToast } from "../../../../util/ToastContext";

type Props = {
    onClick: (searchQuery: SearchQuery) => void;
    searchQuery: SearchQuery;
    isLoading?: boolean;
    onLocationStart?: () => void;
    onLocationError?: (error: string) => void;
    onLocationSuccess?: () => void;
}

const getSelectedState = (searchQuery: SearchQuery): boolean => "gpsLocationSearch" in searchQuery.filters.location;

const GpsPill: FunctionComponent<Props> = ({
    onClick,
    searchQuery,
    isLoading = false,
    onLocationStart,
    onLocationError,
    onLocationSuccess,
    ...props
}) => {
    const { showToast } = useToast();
    const isRequestingLocation = useRef(false);
    
    const handleGpsToggle = () => {
        if (!getSelectedState(searchQuery)) {
            // Prevent multiple concurrent location requests
            if (isRequestingLocation.current) {
                return;
            }
            
            // Check if geolocation is supported
            if (!navigator.geolocation) {
                showToast('Geolocation is not supported by this browser', 'error');
                return;
            }

            // Mark that we're requesting location
            isRequestingLocation.current = true;
            
            // Signal that location fetch is starting
            fetchingData.emit('start');
            onLocationStart?.();

            const handleLocationSuccess = ({ coords }) => {
                const { latitude, longitude } = coords;

                const updatedLocation: GpsLocation = {
                    gpsLocationSearch: [longitude, latitude]
                };

                const updatedSearchQuery = updateSearchQuery({
                    location: updatedLocation
                })(searchQuery);

                fetchingData.emit('end');
                isRequestingLocation.current = false;
                onLocationSuccess?.();
                onClick(updatedSearchQuery);
            };

            const handleLocationError = (error, isRetry = false) => {
                console.error('Geolocation error:', error);
                
                let errorMessage = '';
                let shouldRetry = false;
                
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = 'Location access was denied. Please check your browser and device location settings.';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = 'Your location could not be determined. This may happen indoors or in areas with poor signal.';
                        shouldRetry = !isRetry; // Only retry once
                        break;
                    case error.TIMEOUT:
                        errorMessage = 'Location request timed out. Please try again.';
                        shouldRetry = !isRetry; // Only retry once
                        break;
                    default:
                        errorMessage = 'Unable to access your location. Please check that location services are enabled on your device.';
                        shouldRetry = !isRetry; // Only retry once
                        break;
                }
                
                if (shouldRetry) {
                    // Retry with lower accuracy and longer timeout
                    console.log('Retrying location with lower accuracy...');
                    navigator.geolocation.getCurrentPosition(
                        handleLocationSuccess,
                        (retryError) => handleLocationError(retryError, true),
                        {
                            enableHighAccuracy: false,
                            timeout: 8000,
                            maximumAge: 60000 // 1 minute
                        }
                    );
                } else {
                    fetchingData.emit('end');
                    isRequestingLocation.current = false;
                    showToast(errorMessage, 'error');
                    onLocationError?.(errorMessage);
                }
            };

            // First attempt with high accuracy
            navigator.geolocation.getCurrentPosition(
                handleLocationSuccess,
                handleLocationError,
                {
                    enableHighAccuracy: true,
                    timeout: 5000, // 5 seconds for GPS
                    maximumAge: 300000 // 5 minutes
                }
            );
        } else {
            const updatedLocation: Localities = {
                searchLocations: []
            };

            const updatedSearchQuery = updateSearchQuery({
                location: updatedLocation
            })(searchQuery);

            onClick(updatedSearchQuery);
        }
    }

    return (
        <FilterPill
            onClick={handleGpsToggle}
            selected={getSelectedState(searchQuery)}
            hideLabel={true}
            icon={<GpsMd />}
            {...props}
        />
    )
}

export default GpsPill;
